/**
 * ENG-7909: the per-agent "account enforcement level" marker.
 *
 * A graduated non-payment enforcement ladder sits below the hard org kill
 * switch (halt): `warn` (nag) and `mute` (intercept). The org-scoped level is
 * resolved server-side from `kill_switches.mode` (see lib/kill-switch.ts) and
 * materialized to the manager on the /host/agents poll, exactly as the
 * `kill_switch` marker rides today. When the manager sees a warn/mute level it
 * writes this tiny marker under the agent's dir (`~/.augmented/<codeName>/
 * account-enforcement.json`); the channel MCP servers read it per admitted human
 * inbound and apply the level's behavior:
 *
 *   - `mute` — reply with the support notice INSTEAD of dispatching to the
 *     (silenced) agent, mirroring the maintenance-mode / weekly-limit gate.
 *   - `warn` — the agent runs normally; the adapter sends this level's notice as
 *     a SEPARATE follow-up after the agent's reply (ENG-7909 Slice 2b).
 *
 * ENG-8541: the notice is no longer one fixed string. Each level carries its own
 * copy, platform-wide-configurable from the admin Kill Switches page and stored
 * at `platform_settings.settings.account_enforcement_copy` — NOT per org. The
 * resolved text rides down with the level (marker `text`, optional); when it is
 * absent every reader falls back to this level's built-in default.
 *
 * `halt` is NOT represented here — it stays the existing kill_switch/paused
 * overlay (the agent is torn down, so there is nothing to warn/mute). `none`
 * clears the marker.
 *
 * The pure shape + serialize/parse + copy live here in core so the manager
 * (apps/cli) and the MCP servers (packages/mcp) share ONE definition and can
 * never drift. The filesystem read (a path + node:fs) is the only host-side
 * piece and lives in packages/mcp's account-enforcement-notice.ts.
 *
 * Unlike the usage-limit marker there is no time-based auto-clear: an account
 * level is cleared only when the manager sees the level drop back to none/halt
 * on a later poll and removes the file. A stale file therefore keeps enforcing
 * until the next poll — the safe direction for a non-payment control.
 */

/** The marker filename written under `~/.augmented/<codeName>/`. */
export const ACCOUNT_ENFORCEMENT_MARKER_FILENAME = 'account-enforcement.json';

const ACCOUNT_ENFORCEMENT_MARKER_VERSION = 1 as const;

/**
 * The soft enforcement levels the marker can carry. `halt`/`none` are never
 * written here (halt is the kill_switch overlay; none clears the file).
 */
export type AccountEnforcementLevel = 'warn' | 'mute';

export interface AccountEnforcementMarker {
  version: 1;
  level: AccountEnforcementLevel;
  /**
   * ENG-8541: the operator-configured notice for this level, resolved control-
   * plane side from `platform_settings.settings.account_enforcement_copy` and
   * carried down the /host/agents poll. Absent when no copy is configured, in
   * which case readers fall back to {@link buildAccountIssueReplyText}'s default.
   *
   * DELIBERATELY OPTIONAL ON VERSION 1 rather than a version 2 field. An unknown
   * version makes {@link parseAccountEnforcementMarker} return null, and null
   * means NO ENFORCEMENT — so bumping the version would make every older MCP
   * bundle silently stop enforcing for the length of a fleet bundle skew. An old
   * parser instead ignores this field and shows the default copy: stale wording
   * is an acceptable degradation, failing open is not.
   */
  text?: string;
}

/**
 * Serialize a marker for the manager to write. `text` is omitted entirely when
 * absent/blank, so an unconfigured fleet keeps writing the exact original shape.
 */
export function serializeAccountEnforcementMarker(
  level: AccountEnforcementLevel,
  text?: string | null,
): string {
  const trimmed = typeof text === 'string' ? text.trim() : '';
  const marker: AccountEnforcementMarker = {
    version: ACCOUNT_ENFORCEMENT_MARKER_VERSION,
    level,
    ...(trimmed ? { text: trimmed } : {}),
  };
  return JSON.stringify(marker);
}

/**
 * Parse a marker's raw JSON. Returns null on any malformed / unknown-version /
 * unknown-level input (never throws) so a corrupt file just disables enforcement
 * rather than breaking inbound handling.
 */
export function parseAccountEnforcementMarker(raw: string): AccountEnforcementMarker | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    (parsed as { version?: unknown }).version !== ACCOUNT_ENFORCEMENT_MARKER_VERSION
  ) {
    return null;
  }
  const level = (parsed as { level?: unknown }).level;
  if (level !== 'warn' && level !== 'mute') return null;
  // A malformed/blank `text` is dropped rather than rejected: the level is the
  // enforcement-bearing field, so bad copy must degrade to the default notice,
  // never to "no marker" (which reads as no enforcement).
  const rawText = (parsed as { text?: unknown }).text;
  const text = typeof rawText === 'string' && rawText.trim() ? rawText.trim() : undefined;
  return {
    version: ACCOUNT_ENFORCEMENT_MARKER_VERSION,
    level,
    ...(text ? { text } : {}),
  };
}

/**
 * ENG-8541: the platform-wide, per-level notice overrides an operator sets on the
 * admin Kill Switches page. Stored at
 * `platform_settings.settings.account_enforcement_copy` (single global row) —
 * there is deliberately NO per-org variant; this is one message for the fleet.
 */
export interface AccountEnforcementCopy {
  warn?: string;
  mute?: string;
}

/**
 * The built-in notice per level, used whenever no override is configured.
 *
 * `warn` and `mute` say different things because they ARE different states: on
 * `warn` the agent still answers and the notice is a nudge appended to its reply,
 * so it must not claim the agent is unavailable; on `mute` the notice REPLACES
 * the agent, so it has to explain the silence. Both use the full brand name per
 * the customer-facing-prose rule and speak in the agent's own first person.
 */
export const DEFAULT_ACCOUNT_ENFORCEMENT_COPY: Record<AccountEnforcementLevel, string> = {
  warn: "Heads-up: there's an issue with your account that needs attention. Please contact support@augmented.team so I can keep helping you.",
  mute: 'There is an issue with your account, please contact support@augmented.team.',
};

/**
 * Normalize an operator-supplied copy object: trims, and drops blank/non-string
 * entries so an empty admin field means "use the default" rather than "post an
 * empty message". Returns undefined when nothing is configured.
 */
export function normalizeAccountEnforcementCopy(raw: unknown): AccountEnforcementCopy | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const pick = (key: AccountEnforcementLevel): string | undefined => {
    const value = (raw as Record<string, unknown>)[key];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  };
  const warn = pick('warn');
  const mute = pick('mute');
  if (!warn && !mute) return undefined;
  return { ...(warn ? { warn } : {}), ...(mute ? { mute } : {}) };
}

/**
 * The customer-facing notice posted while an org is under account enforcement.
 *
 * `override` is the resolved marker text (operator-configured, ENG-8541); a
 * blank/absent override falls back to this level's built-in default, so an
 * unconfigured platform behaves exactly as it did before ENG-8541.
 */
export function buildAccountIssueReplyText(
  level: AccountEnforcementLevel,
  override?: string | null,
): string {
  const trimmed = typeof override === 'string' ? override.trim() : '';
  return trimmed || DEFAULT_ACCOUNT_ENFORCEMENT_COPY[level];
}
